// Cloud's part of Settings → Notifications: where notices go. One box: this
// device first (this browser's web push, inside the desktop app this desktop,
// inside the iOS app this app), then every other destination of the person,
// each with remove.
//
// Promise:
// - "This device" is found by matching, never by a remembered id: the
//   browser's subscription endpoint (or the desktop's installation id)
//   against the server's list. Not on the list means not receiving, so the
//   switch shows off rather than claim something untrue.
// - What keeps this device from receiving is said in place of the switch:
//   no web push on this server, an iPhone outside the home screen, a browser
//   without push, notifications blocked. Inside the desktop app a server
//   without web push is no reason (desktops are told over the live stream).
// - While the desktop app cannot show mac notifications, the reason (and a
//   way to System Settings) replaces the test button; the switch stays.
// - Failures are said under the row that was pressed; a removed row leaves
//   its line at the head of the box.
import { useQuery } from "@tanstack/react-query";
import type { PushSubscriptionView } from "@trameria/cloud-api/notices-shared";
import { m } from "@trameria/cloud-i18n";
import {
  type AppNoticeHost,
  apiErrorMessage,
  appNoticeHost,
  Button,
  type DesktopNotificationStatus,
  ErrorLine,
  formatRelative,
  noticeDesktop,
  SettingsBox,
  SettingsRow,
  SettingsSection,
  Skeleton,
  Switch,
  toast,
  useNoticeDesktop,
} from "@trameria/ui-core";
import { useMemo, useState, useSyncExternalStore } from "react";
import { DESKTOP_LABEL } from "./push";
import {
  appNoticesQuery,
  browserPushQuery,
  usePushState,
  useRemoveDestination,
  useSendTest,
  useSetApp,
  useSetDesktop,
  useSubscribeBrowser,
  useUnsubscribeBrowser,
} from "./queries";

/** "Mac · Chrome" from a user agent; the order of the checks matters. */
export function deviceLabel(userAgent: string | null): string {
  const ua = userAgent ?? "";
  const os = /iPad/.test(ua)
    ? "iPad"
    : /iPhone|iPod/.test(ua)
      ? "iPhone"
      : /Android/.test(ua)
        ? "Android"
        : /Macintosh/.test(ua)
          ? "Mac"
          : /Windows/.test(ua)
            ? "Windows"
            : /Linux/.test(ua)
              ? "Linux"
              : "";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\/|CriOS\//.test(ua)
      ? "Chrome"
      : /Firefox\/|FxiOS\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "";
  // Names of systems and browsers are not translated.
  if (os && browser) return `${os} · ${browser}`;
  return os || browser || m.push_other_device();
}

const rowLabel = (row: PushSubscriptionView) =>
  row.kind === "desktop"
    ? row.label || DESKTOP_LABEL
    : row.kind === "app"
      ? m.push_app_label({ device: row.label || "iPhone" })
      : deviceLabel(row.label);

export const pushSections = [
  { id: "destinations", label: () => m.push_destinations() },
];

/** The destinations under their heading (the old web's "where notices go"). */
export function PushSettings() {
  return (
    <SettingsSection id="destinations" label={m.push_destinations()}>
      <Destinations />
    </SettingsSection>
  );
}

function Destinations() {
  const push = usePushState();
  const remove = useRemoveDestination();
  const [removed, setRemoved] = useState<string | null>(null);
  const desktop = useNoticeDesktop();
  const app = useMemo(() => appNoticeHost(), []);
  const endpoint = useBrowserEndpoint(desktop === null && app === null);
  if (push.isError)
    return (
      <div className="mt-[14px] flex items-center gap-3 text-[13px] text-muted">
        {m.web_load_failed()}
        <Button onClick={() => void push.refetch()}>{m.web_retry()}</Button>
      </div>
    );
  if (!push.data)
    return (
      <div className="mt-[14px]">
        <Skeleton lines={[70, 50]} />
      </div>
    );
  return (
    <>
      {remove.isError && removed && (
        <div className="mt-[14px]">
          <ErrorLine>{m.push_remove_failed({ name: removed })}</ErrorLine>
        </div>
      )}
      <SettingsBox>
        {app ? (
          <ThisAppRow host={app} />
        ) : desktop ? (
          <ThisDesktopRow installationId={desktop.installationId} />
        ) : (
          <ThisBrowserRow />
        )}
        {others(push.data.subscriptions, {
          installationId:
            app?.notices.installationId ?? desktop?.installationId ?? null,
          endpoint,
        }).map((row) => (
          <SettingsRow
            key={row.id}
            label={rowLabel(row)}
            description={
              row.kind === "desktop"
                ? m.push_desktop_app()
                : row.lastSuccessAt
                  ? m.push_delivered_at({
                      when: formatRelative(row.lastSuccessAt),
                    })
                  : m.push_not_delivered()
            }
          >
            <Button
              variant="ghost"
              onClick={() => {
                const name = rowLabel(row);
                setRemoved(name);
                remove.mutate(row, {
                  onSuccess: () => toast(m.push_removed({ name })),
                });
              }}
            >
              {m.push_remove()}
            </Button>
          </SettingsRow>
        ))}
      </SettingsBox>
    </>
  );
}

/** Every destination but this device's. */
function others(
  rows: readonly PushSubscriptionView[],
  self: { installationId: string | null; endpoint: string | null },
) {
  return rows.filter((r) =>
    r.kind === "webpush"
      ? self.endpoint === null || r.endpoint !== self.endpoint
      : r.id !== self.installationId,
  );
}

function useBrowserEndpoint(enabled: boolean): string | null {
  const browser = useQuery({ ...browserPushQuery(), enabled });
  return browser.data?.kind === "subscribed" ? browser.data.endpoint : null;
}

function ThisBrowserRow() {
  const push = usePushState();
  const browser = useQuery(browserPushQuery());
  const subscribe = useSubscribeBrowser();
  const unsubscribe = useUnsubscribeBrowser();
  const test = useSendTest();
  const endpoint = useBrowserEndpoint(true);
  const mine = endpoint
    ? push.data?.subscriptions.find(
        (s) => s.kind === "webpush" && s.endpoint === endpoint,
      )
    : undefined;
  const publicKey = push.data?.publicKey ?? null;
  const reason =
    publicKey === null
      ? m.push_unavailable()
      : browser.data?.kind === "unsupported"
        ? browser.data.ios
          ? m.push_ios_add_to_home()
          : m.push_unsupported()
        : browser.data?.kind === "denied"
          ? m.push_blocked()
          : null;
  const busy = subscribe.isPending || unsubscribe.isPending;
  return (
    <SettingsRow
      label={deviceLabel(navigator.userAgent)}
      description={m.push_this_device()}
    >
      <div className="flex flex-col items-end">
        {reason ? (
          // The old web's: a paragraph read from its left, 13 px, faint.
          <p className="text-left text-[13px] leading-[1.5] text-faint">
            {reason}
          </p>
        ) : (
          <div className="flex items-center gap-3">
            <TestButton id={mine?.id ?? null} test={test} />
            <Switch
              label={m.push_receive()}
              checked={Boolean(mine)}
              disabled={busy || !browser.data}
              onChange={(next) => {
                if (!next) unsubscribe.mutate();
                else if (publicKey) subscribe.mutate(publicKey);
              }}
            />
          </div>
        )}
        <TestResult test={test} />
        {subscribe.isError && (
          <ErrorLine>
            {apiErrorMessage(subscribe.error, m.push_subscribe_failed())}
          </ErrorLine>
        )}
        {unsubscribe.isError && (
          <ErrorLine>
            {apiErrorMessage(unsubscribe.error, m.push_unsubscribe_failed())}
          </ErrorLine>
        )}
      </div>
    </SettingsRow>
  );
}

const subscribeStatus = (onChange: () => void) =>
  noticeDesktop()?.onNotificationStatusChange?.(onChange) ?? (() => {});
const statusNow = (): DesktopNotificationStatus =>
  noticeDesktop()?.notificationStatus?.() ?? "ok";

function ThisDesktopRow({ installationId }: { installationId: string }) {
  const push = usePushState();
  const set = useSetDesktop();
  const test = useSendTest();
  const status = useSyncExternalStore(subscribeStatus, statusNow, () => "ok");
  const mine = push.data?.subscriptions.find(
    (s) => s.kind === "desktop" && s.id === installationId,
  );
  const macReason = !mine
    ? null
    : status === "blocked"
      ? m.push_desktop_blocked()
      : status === "unavailable"
        ? m.push_desktop_unsigned()
        : null;
  return (
    <SettingsRow
      label={mine?.label || DESKTOP_LABEL}
      description={m.push_this_device()}
    >
      <div className="flex flex-col items-end">
        <div className="flex items-center gap-3">
          {macReason ? (
            <>
              <span className="text-right text-[12px] leading-[1.6] text-muted">
                {macReason}
              </span>
              {status === "blocked" && (
                <Button
                  variant="ghost"
                  onClick={() => noticeDesktop()?.openNotificationSettings?.()}
                >
                  {m.push_desktop_open_settings()}
                </Button>
              )}
            </>
          ) : (
            <TestButton id={mine?.id ?? null} test={test} />
          )}
          <Switch
            label={m.push_receive()}
            checked={Boolean(mine)}
            disabled={set.isPending}
            onChange={(on) => set.mutate({ installationId, on })}
          />
        </div>
        <TestResult test={test} />
        {set.isError && (
          <ErrorLine>
            {apiErrorMessage(
              set.error,
              set.variables?.on
                ? m.push_subscribe_failed()
                : m.push_unsubscribe_failed(),
            )}
          </ErrorLine>
        )}
      </div>
    </SettingsRow>
  );
}

/**
 * Inside the iOS app: the app is this device. Its switch registers the app's
 * token, asking the person first (or, refused before, the app opens its
 * notification settings); refused, the reason and the way to its settings
 * stand in place of the switch.
 */
function ThisAppRow({ host }: { host: AppNoticeHost }) {
  const push = usePushState();
  const state = useQuery(appNoticesQuery(host));
  const set = useSetApp(host);
  const test = useSendTest();
  const device = host.notices.device;
  const mine = push.data?.subscriptions.find(
    (s) => s.kind === "app" && s.id === host.notices.installationId,
  );
  const unavailable = push.data?.appPush === false;
  const denied = state.data?.status === "denied";
  return (
    <SettingsRow
      label={m.push_app_label({ device })}
      description={m.push_this_device()}
    >
      <div className="flex flex-col items-end">
        {unavailable ? (
          <p className="text-left text-[13px] leading-[1.5] text-faint">
            {m.push_unavailable()}
          </p>
        ) : denied ? (
          <div className="flex items-center gap-3">
            <span className="text-right text-[12px] leading-[1.6] text-muted">
              {m.push_app_denied({ device })}
            </span>
            <Button
              variant="ghost"
              disabled={set.isPending}
              onClick={() => set.mutate(true)}
            >
              {m.push_app_open_settings()}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <TestButton id={mine?.id ?? null} test={test} />
            <Switch
              label={m.push_receive()}
              checked={Boolean(mine)}
              disabled={set.isPending || state.isPending}
              onChange={(on) => set.mutate(on)}
            />
          </div>
        )}
        <TestResult test={test} />
        {set.isError && (
          <ErrorLine>
            {apiErrorMessage(
              set.error,
              set.variables
                ? m.push_subscribe_failed()
                : m.push_unsubscribe_failed(),
            )}
          </ErrorLine>
        )}
      </div>
    </SettingsRow>
  );
}

/**
 * Always drawn, hidden while this device does not receive: appearing and
 * disappearing changed the row's width and moved the rows below on a phone.
 */
function TestButton({
  id,
  test,
}: {
  id: string | null;
  test: ReturnType<typeof useSendTest>;
}) {
  const shown = id !== null;
  return (
    <Button
      variant="ghost"
      inert={!shown || undefined}
      aria-hidden={!shown || undefined}
      className={shown ? "" : "pointer-events-none opacity-0"}
      disabled={test.isPending}
      onClick={() => {
        if (id)
          test.mutate(id, {
            onSuccess: ({ sent }) => {
              if (sent > 0) toast(m.push_sent());
            },
          });
      }}
    >
      {m.push_send_test()}
    </Button>
  );
}

function TestResult({ test }: { test: ReturnType<typeof useSendTest> }) {
  if (test.isError)
    return (
      <ErrorLine>{apiErrorMessage(test.error, m.push_test_failed())}</ErrorLine>
    );
  if (test.isSuccess && test.data.sent === 0)
    return <ErrorLine>{m.push_not_sent()}</ErrorLine>;
  return null;
}
